import { apiClient } from '@/lib/apiClient';
import type {
  ApiResponse,
  KnowledgeBaseEntry,
  KnowledgeBaseInitPreview,
  KnowledgeBaseContextSnapshot,
  Priority,
  PriorityCreate,
  PriorityUpdate,
  Status,
  StatusCreate,
  StatusUpdate,
  Tag,
  TagCreate,
  TagUpdate,
  Workspace,
  WorkspaceCreate,
  WorkspaceMember,
  WorkspaceRole,
  WorkspaceUpdate
} from "dico-types";

export type WorkspaceWithRole = Workspace & { role: WorkspaceRole };

// ============================================================================
// WORKSPACE API FUNCTIONS
// ============================================================================

/**
 * List all workspaces the current user belongs to.
 * @param signal - Optional abort signal.
 */
const getWorkspaces = (signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<WorkspaceWithRole[]>>('/api/workspaces', { signal });
};

/**
 * Get a workspace by ID.
 * @param workspaceId - The workspace ID.
 * @param signal - Optional abort signal.
 */
const getWorkspaceById = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<WorkspaceWithRole>>(`/api/workspaces/${workspaceId}`, {
    signal
  });
};

/**
 * Create a new workspace.
 * @param data - Workspace creation payload.
 * @param signal - Optional abort signal.
 */
const createWorkspace = (data: WorkspaceCreate, signal?: AbortSignal) => {
  return apiClient.post<ApiResponse<WorkspaceWithRole>>('/api/workspaces', data, { signal });
};

/**
 * Update a workspace.
 * @param workspaceId - The workspace ID.
 * @param data - Fields to update.
 * @param signal - Optional abort signal.
 */
const updateWorkspace = (workspaceId: string, data: WorkspaceUpdate, signal?: AbortSignal) => {
  return apiClient.put<ApiResponse<Workspace>>(`/api/workspaces/${workspaceId}`, data, { signal });
};

/**
 * Delete a workspace.
 * @param workspaceId - The workspace ID.
 * @param signal - Optional abort signal.
 */
const deleteWorkspace = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.delete<ApiResponse>(`/api/workspaces/${workspaceId}`, { signal });
};

/**
 * List members of a workspace.
 * @param workspaceId - The workspace ID.
 * @param signal - Optional abort signal.
 */
const getWorkspaceMembers = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<WorkspaceMember[]>>(`/api/workspaces/${workspaceId}/members`, {
    signal
  });
};

/**
 * List statuses for a workspace (for ticket views and filters).
 * @param workspaceId - The workspace ID.
 * @param signal - Optional abort signal.
 */
const getWorkspaceStatuses = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<Status[]>>(`/api/workspaces/${workspaceId}/statuses`, {
    signal
  });
};

const createStatus = (
  workspaceId: string,
  data: Omit<StatusCreate, 'workspaceId'>,
  signal?: AbortSignal
) =>
  apiClient.post<ApiResponse<Status>>(`/api/workspaces/${workspaceId}/statuses`, data, { signal });

const updateStatus = (
  workspaceId: string,
  statusId: string,
  data: StatusUpdate,
  signal?: AbortSignal
) =>
  apiClient.put<ApiResponse<Status>>(
    `/api/workspaces/${workspaceId}/statuses/${statusId}`,
    data,
    { signal }
  );

const deleteStatus = (workspaceId: string, statusId: string, signal?: AbortSignal) =>
  apiClient.delete<ApiResponse>(`/api/workspaces/${workspaceId}/statuses/${statusId}`, {
    signal
  });

/**
 * List tags (labels) for a workspace.
 * @param workspaceId - The workspace ID.
 * @param signal - Optional abort signal.
 */
const getWorkspaceTags = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<Tag[]>>(`/api/workspaces/${workspaceId}/tags`, { signal });
};

const createTag = (
  workspaceId: string,
  data: Omit<TagCreate, 'workspaceId'>,
  signal?: AbortSignal
) => apiClient.post<ApiResponse<Tag>>(`/api/workspaces/${workspaceId}/tags`, data, { signal });

const updateTag = (
  workspaceId: string,
  tagId: string,
  data: TagUpdate,
  signal?: AbortSignal
) =>
  apiClient.put<ApiResponse<Tag>>(`/api/workspaces/${workspaceId}/tags/${tagId}`, data, {
    signal
  });

const deleteTag = (workspaceId: string, tagId: string, signal?: AbortSignal) =>
  apiClient.delete<ApiResponse>(`/api/workspaces/${workspaceId}/tags/${tagId}`, { signal });

/**
 * List priorities for a workspace.
 * @param workspaceId - The workspace ID.
 * @param signal - Optional abort signal.
 */
const getWorkspacePriorities = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<Priority[]>>(`/api/workspaces/${workspaceId}/priorities`, {
    signal
  });
};

const createPriority = (
  workspaceId: string,
  data: Omit<PriorityCreate, 'workspaceId'>,
  signal?: AbortSignal
) =>
  apiClient.post<ApiResponse<Priority>>(`/api/workspaces/${workspaceId}/priorities`, data, {
    signal
  });

const updatePriority = (
  workspaceId: string,
  priorityId: string,
  data: PriorityUpdate,
  signal?: AbortSignal
) =>
  apiClient.put<ApiResponse<Priority>>(
    `/api/workspaces/${workspaceId}/priorities/${priorityId}`,
    data,
    { signal }
  );

const deletePriority = (workspaceId: string, priorityId: string, signal?: AbortSignal) =>
  apiClient.delete<ApiResponse>(`/api/workspaces/${workspaceId}/priorities/${priorityId}`, {
    signal
  });

// ============================================================================
// KNOWLEDGE BASE
// ============================================================================

/**
 * List knowledge base entries for a workspace.
 * @param workspaceId - The workspace ID.
 * @param signal - Optional abort signal.
 */
const getKnowledgeBaseEntries = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<KnowledgeBaseEntry[]>>(
    `/api/workspaces/${workspaceId}/knowledge-base`,
    { signal }
  );
};

const createKnowledgeBaseEntry = (
  workspaceId: string,
  data: { key: string; title: string; content: string; tags?: string[] },
  signal?: AbortSignal
) => {
  return apiClient.post<ApiResponse<KnowledgeBaseEntry>>(
    `/api/workspaces/${workspaceId}/knowledge-base`,
    data,
    { signal }
  );
};

const updateKnowledgeBaseEntry = (
  workspaceId: string,
  key: string,
  data: Partial<Pick<KnowledgeBaseEntry, 'title' | 'content' | 'tags'>>,
  signal?: AbortSignal
) => {
  return apiClient.put<ApiResponse<KnowledgeBaseEntry>>(
    `/api/workspaces/${workspaceId}/knowledge-base/${encodeURIComponent(key)}`,
    {
      title: data.title,
      content: data.content,
      tags: data.tags
    },
    { signal }
  );
};

const deleteKnowledgeBaseEntry = (workspaceId: string, key: string, signal?: AbortSignal) => {
  return apiClient.delete<ApiResponse>(
    `/api/workspaces/${workspaceId}/knowledge-base/${encodeURIComponent(key)}`,
    { signal }
  );
};

const initKnowledgeBase = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.post<ApiResponse<{ snapshotId: string; status: string }>>(
    `/api/workspaces/${workspaceId}/knowledge-base/init`,
    {},
    { signal }
  );
};

const getKnowledgeBaseSnapshot = (workspaceId: string, snapshotId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<KnowledgeBaseContextSnapshot>>(
    `/api/workspaces/${workspaceId}/knowledge-base/snapshots/${snapshotId}`,
    { signal }
  );
};

const getLatestKnowledgeBaseSnapshot = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<KnowledgeBaseContextSnapshot>>(
    `/api/workspaces/${workspaceId}/knowledge-base/snapshots/latest`,
    { signal }
  );
};

const backfillTickets = (
  workspaceId: string,
  options: {
    daysBack?: number;
    maxMessagesPerChannel?: number;
    skipNotifications?: boolean;
  } = {},
  signal?: AbortSignal
) => {
  return apiClient.post<
    ApiResponse<{
      messagesSynced: number;
      ticketsCreated: number;
      errors: Array<{ message: string; error: string }>;
    }>
  >(`/api/workspaces/${workspaceId}/tickets/sync`, options, { signal });
};

export const workspaceApi = {
  getWorkspaces,
  getWorkspaceById,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
  getWorkspaceMembers,
  getWorkspaceStatuses,
  createStatus,
  updateStatus,
  deleteStatus,
  getWorkspaceTags,
  createTag,
  updateTag,
  deleteTag,
  getWorkspacePriorities,
  createPriority,
  updatePriority,
  deletePriority,
  getKnowledgeBaseEntries,
  createKnowledgeBaseEntry,
  updateKnowledgeBaseEntry,
  deleteKnowledgeBaseEntry,
  initKnowledgeBase,
  getKnowledgeBaseSnapshot,
  getLatestKnowledgeBaseSnapshot,
  backfillTickets
};

export {
  getWorkspaces,
  getWorkspaceById,
  createWorkspace,
  updateWorkspace,
  deleteWorkspace,
  getWorkspaceMembers,
  getWorkspaceStatuses,
  createStatus,
  updateStatus,
  deleteStatus,
  getWorkspaceTags,
  createTag,
  updateTag,
  deleteTag,
  getWorkspacePriorities,
  createPriority,
  updatePriority,
  deletePriority,
  getKnowledgeBaseEntries,
  createKnowledgeBaseEntry,
  updateKnowledgeBaseEntry,
  deleteKnowledgeBaseEntry,
  initKnowledgeBase,
  getKnowledgeBaseSnapshot,
  getLatestKnowledgeBaseSnapshot,
  backfillTickets
};
